import * as THREE from 'three';
import {
  pieceOrigin,
  pieceScale,
  TILE_SIZE,
  type ArenaMap,
  type MapEnv,
  type ParticleKind,
  type PieceId,
} from '@arena/shared';

/** Real seconds per day in the hub; every client reads the wall clock, so they agree on the hour. */
const DAY_SECONDS = 30 * 60;
const SKY_RADIUS = 150;
const STAR_COUNT = 900;
/** Where the sun sits on maps without a day cycle. */
const FIXED_SUN = new THREE.Vector3(25, 45, 15).normalize();
/** Low suns cast shadows longer than the shadow camera covers; the light never drops below this. */
const MIN_LIGHT_ELEVATION = 0.45;

/** Shared clock for every ambient shader (sky, glows, particles, swaying trees), in seconds. */
const ambientTime = { value: 0 };

export type Shadows = 'off' | 'low' | 'high';

export interface Graphics {
  renderScale: number;
  antialias: boolean;
  shadows: Shadows;
}

const HIGH: Graphics = { renderScale: 1, antialias: true, shadows: 'high' };

export const pixelRatio = (renderScale: number) =>
  Math.min(window.devicePixelRatio, 2) * renderScale;

/** Antialiasing and shadows are fixed for the renderer's life; the render scale can change. */
export function createRenderer(
  canvas: HTMLCanvasElement,
  graphics = HIGH,
): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: graphics.antialias,
    // Dual-GPU laptops otherwise often get the integrated one.
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(pixelRatio(graphics.renderScale));
  renderer.shadowMap.enabled = graphics.shadows !== 'off';
  renderer.shadowMap.type =
    graphics.shadows === 'high' ? THREE.PCFSoftShadowMap : THREE.PCFShadowMap;
  renderer.toneMapping = THREE.AgXToneMapping;
  return renderer;
}

/** Everything the light and sky take from the time of day. */
export interface Sky {
  zenith: THREE.Color;
  /** Also the fog, so distant geometry melts into it. */
  horizon: THREE.Color;
  sun: THREE.Color;
  sunIntensity: number;
  hemiSky: THREE.Color;
  hemiGround: THREE.Color;
  hemiIntensity: number;
  /** 0 by day, 1 at night: stars, the moon, lamp glow, fireflies. */
  night: number;
}

const hexSky = (
  zenith: string,
  horizon: string,
  sun: string,
  sunIntensity: number,
  hemiSky: string,
  hemiGround: string,
  hemiIntensity: number,
  night: number,
): Sky => ({
  zenith: new THREE.Color(zenith),
  horizon: new THREE.Color(horizon),
  sun: new THREE.Color(sun),
  sunIntensity,
  hemiSky: new THREE.Color(hemiSky),
  hemiGround: new THREE.Color(hemiGround),
  hemiIntensity,
  night,
});

// prettier-ignore
const NIGHT = hexSky('#03050d', '#121a30', '#9db0ff', 1, '#5b6ca6', '#1c1a26', 1.15, 1);
// prettier-ignore
const DAWN = hexSky('#34406e', '#e0937a', '#ffb27a', 1.6, '#c9a9b8', '#4a3a36', 1.1, 0.3);
// prettier-ignore
const DUSK = hexSky('#272a5c', '#e8805a', '#ff9150', 1.8, '#c093a4', '#4a3430', 1.0, 0.4);

/** The map's own look: what static maps always show and the hub shows at midday. */
export function daySky(env: MapEnv = {}): Sky {
  const horizon = new THREE.Color(env.sky ?? '#8fb4d9');
  const hsl = horizon.getHSL({ h: 0, s: 0, l: 0 }, THREE.SRGBColorSpace);
  const light = env.light ?? 1;
  // A dark sky means a night map, lit by a cool ambient like the hub's night.
  const night = 1 - THREE.MathUtils.smoothstep(hsl.l, 0.15, 0.35);
  return {
    zenith: new THREE.Color().setHSL(
      hsl.h,
      Math.min(1, hsl.s + 0.15),
      hsl.l * 0.65,
      THREE.SRGBColorSpace,
    ),
    horizon,
    sun: new THREE.Color(env.sun ?? '#fff1d6'),
    sunIntensity: 2.2 * light,
    hemiSky: new THREE.Color(0xdfeeff).lerp(NIGHT.hemiSky, night * 0.5),
    hemiGround: new THREE.Color(0x5a4a3a).lerp(NIGHT.hemiGround, night * 0.5),
    hemiIntensity: 1.6 * light,
    night,
  };
}

/** Writes the sky at `hour` (0-24) into `out`, easing between night, dawn, the map's day and dusk. */
export function skyAt(hour: number, day: Sky, out: Sky): Sky {
  const keys: [number, Sky][] = [
    [0, NIGHT],
    [5, NIGHT],
    [6.5, DAWN],
    [8.5, day],
    [15.5, day],
    [17.5, DUSK],
    [19, NIGHT],
    [24, NIGHT],
  ];
  const h = THREE.MathUtils.euclideanModulo(hour, 24);
  const i = keys.findIndex(([at]) => at > h);
  const [h0, a] = keys[i - 1];
  const [h1, b] = keys[i];
  const t = THREE.MathUtils.smoothstep(h, h0, h1);
  out.zenith.lerpColors(a.zenith, b.zenith, t);
  out.horizon.lerpColors(a.horizon, b.horizon, t);
  out.sun.lerpColors(a.sun, b.sun, t);
  out.hemiSky.lerpColors(a.hemiSky, b.hemiSky, t);
  out.hemiGround.lerpColors(a.hemiGround, b.hemiGround, t);
  out.sunIntensity = THREE.MathUtils.lerp(a.sunIntensity, b.sunIntensity, t);
  out.hemiIntensity = THREE.MathUtils.lerp(a.hemiIntensity, b.hemiIntensity, t);
  out.night = THREE.MathUtils.lerp(a.night, b.night, t);
  return out;
}

/** Toward the sun: it rises in +x at 6, peaks at noon and sets at 18. */
export const sunDirection = (hour: number, out: THREE.Vector3) => {
  const angle = ((hour - 6) / 24) * Math.PI * 2;
  return out.set(Math.cos(angle), Math.sin(angle), 0.6).normalize();
};

const hourNow = (): number => {
  // `?hour=21` pins the hub's clock, for previewing the night.
  const pinned = new URLSearchParams(globalThis.location?.search).get('hour');
  if (pinned !== null) return Number(pinned);
  return ((Date.now() / 1000 / DAY_SECONDS) * 24) % 24;
};

// -------------------------------------------------------------------------------------------
// Lamps
// -------------------------------------------------------------------------------------------

interface Lamp {
  /** Flame positions in model units. */
  at: [number, number, number][];
  color: string;
  /** Glow radius in world units. */
  size: number;
  /** Glow by daylight, against 1 at night: open fires stay bright, lanterns fade. */
  day: number;
}

const LAMPS: Partial<Record<PieceId, Lamp>> = {
  'graveyard/lightpost-single': {
    at: [[0, 1.06, 0.26]],
    color: '#ffb85c',
    size: 1.8,
    day: 0.1,
  },
  'graveyard/lantern-candle': {
    at: [[0, 0.2, 0]],
    color: '#ffa44a',
    size: 1.1,
    day: 0.15,
  },
  'graveyard/candle-multiple': {
    at: [[0, 0.2, 0]],
    color: '#ffa44a',
    size: 0.8,
    day: 0.3,
  },
  // The basket holds no flame of its own; the glow is the fire.
  'graveyard/fire-basket': {
    at: [[0, 0.2, 0]],
    color: '#ff7a2e',
    size: 1.8,
    day: 0.75,
  },
};

const LAMP_VERTEX = /* glsl */ `
  attribute vec3 center;
  attribute vec3 tint;
  attribute float size;
  attribute float seed;
  attribute float day;
  uniform float time;
  uniform float night;
  uniform bool ground;
  varying vec2 vUv;
  varying vec3 vColor;
  void main() {
    float flicker = 0.88 + 0.12 * sin(time * 9.0 + seed * 17.0) * sin(time * 5.3 + seed * 5.0);
    vUv = position.xy * 2.0;
    vColor = tint * flicker * (ground ? 0.6 * night : mix(day, 1.0, night));
    if (ground) {
      gl_Position = projectionMatrix * viewMatrix * vec4(center + vec3(position.x, 0.0, position.y) * size * 3.0, 1.0);
      return;
    }
    vec4 view = viewMatrix * vec4(center, 1.0);
    // Pulled toward the camera so the lamp's own mesh doesn't hide the glow.
    view.xyz += normalize(-view.xyz) * size * 0.4;
    view.xy += position.xy * size * flicker;
    gl_Position = projectionMatrix * view;
  }
`;

const LAMP_FRAGMENT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vColor;
  void main() {
    float falloff = max(1.0 - length(vUv), 0.0);
    gl_FragColor = vec4(vColor, falloff * falloff);
    #include <colorspace_fragment>
  }
`;

/** Glow quads facing the camera at every flame, or pools of light on the ground beneath them. */
function lampMesh(
  map: Pick<ArenaMap, 'pieces'>,
  ground: boolean,
  night: { value: number },
): THREE.Mesh | null {
  const centers: number[] = [];
  const tints: number[] = [];
  const sizes: number[] = [];
  const days: number[] = [];
  const point = new THREE.Vector3();
  const color = new THREE.Color();
  for (const placed of map.pieces) {
    const lamp = LAMPS[placed.piece];
    if (!lamp) continue;
    const origin = pieceOrigin(placed);
    for (const at of lamp.at) {
      point
        .fromArray(at)
        .multiplyScalar(pieceScale(placed))
        .applyAxisAngle(THREE.Object3D.DEFAULT_UP, (placed.rot * Math.PI) / 2)
        .add(origin);
      if (ground) point.y = origin.y + 0.03;
      centers.push(point.x, point.y, point.z);
      tints.push(...color.set(lamp.color).toArray());
      sizes.push(lamp.size);
      days.push(lamp.day);
    }
  }
  if (!sizes.length) return null;
  const quad = new THREE.PlaneGeometry(1, 1);
  const geometry = new THREE.InstancedBufferGeometry();
  geometry.setIndex(quad.index);
  geometry.setAttribute('position', quad.getAttribute('position'));
  geometry.instanceCount = sizes.length;
  const instanced = (values: number[], size: number) =>
    new THREE.InstancedBufferAttribute(new Float32Array(values), size);
  geometry.setAttribute('center', instanced(centers, 3));
  geometry.setAttribute('tint', instanced(tints, 3));
  geometry.setAttribute('size', instanced(sizes, 1));
  geometry.setAttribute('day', instanced(days, 1));
  geometry.setAttribute(
    'seed',
    instanced(
      sizes.map(() => Math.random()),
      1,
    ),
  );
  const mesh = new THREE.Mesh(
    geometry,
    new THREE.ShaderMaterial({
      vertexShader: LAMP_VERTEX,
      fragmentShader: LAMP_FRAGMENT,
      uniforms: {
        time: ambientTime,
        night,
        ground: { value: ground },
      },
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      polygonOffset: ground,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4,
    }),
  );
  mesh.frustumCulled = false;
  return mesh;
}

// -------------------------------------------------------------------------------------------
// Particles
// -------------------------------------------------------------------------------------------

interface ParticleDef {
  count: number;
  color: string;
  /** World units. */
  size: number;
  /** Particles fill a box this wide and tall around the focus, wrapping as it moves. */
  box: number;
  height: number;
  /** World units per second. */
  drift: [number, number, number];
  wobble: number;
  /** 0 steady, 1 blinks fully off between pulses. */
  blink: number;
  opacity: number;
  /** When they're out: dust in daylight, fireflies after dark. */
  shows: 'day' | 'night' | 'always';
}

const PARTICLES: Record<ParticleKind, ParticleDef> = {
  dust: {
    count: 260,
    color: '#fff1cf',
    size: 0.05,
    box: 26,
    height: 6,
    drift: [0.12, 0.03, 0.06],
    wobble: 0.35,
    blink: 0,
    opacity: 0.45,
    shows: 'day',
  },
  fireflies: {
    count: 180,
    color: '#d4ff70',
    size: 0.14,
    box: 30,
    height: 3.5,
    drift: [0, 0, 0],
    wobble: 0.9,
    blink: 1,
    opacity: 1,
    shows: 'night',
  },
  embers: {
    count: 260,
    color: '#ff7a2e',
    size: 0.09,
    box: 30,
    height: 9,
    drift: [0.15, 0.55, 0.05],
    wobble: 0.45,
    blink: 0.5,
    opacity: 0.9,
    shows: 'always',
  },
};

const PARTICLE_VERTEX = /* glsl */ `
  attribute float seed;
  uniform float time;
  uniform float size;
  uniform float box;
  uniform float height;
  uniform float wobble;
  uniform float blink;
  uniform float viewHeight;
  uniform vec3 focus;
  uniform vec3 drift;
  varying float vAlpha;
  void main() {
    vec3 p = position * vec3(box, height, box) + drift * time + wobble * vec3(
      sin(time * 0.6 + seed * 31.0),
      0.5 * sin(time * 0.8 + seed * 17.0),
      cos(time * 0.5 + seed * 23.0)
    );
    vec2 offset = mod(p.xz - focus.xz + box * 0.5, box) - box * 0.5;
    float y = mod(p.y - focus.y + 1.5, height);
    vec3 world = vec3(focus.x + offset.x, focus.y - 1.5 + y, focus.z + offset.y);
    // Fade out near the edges of the box so wrapping never pops.
    vAlpha = (1.0 - smoothstep(0.3, 0.5, length(offset) / box))
      * smoothstep(0.0, 0.15, y / height) * (1.0 - smoothstep(0.85, 1.0, y / height))
      * mix(1.0, pow(0.5 + 0.5 * sin(time * 1.3 + seed * 40.0), 3.0), blink);
    vec4 view = viewMatrix * vec4(world, 1.0);
    gl_Position = projectionMatrix * view;
    gl_PointSize = max(1.0, size * viewHeight * projectionMatrix[1][1] * 0.5 / -view.z);
  }
`;

const PARTICLE_FRAGMENT = /* glsl */ `
  uniform vec3 color;
  uniform float opacity;
  varying float vAlpha;
  void main() {
    float falloff = max(1.0 - length(gl_PointCoord - 0.5) * 2.0, 0.0);
    gl_FragColor = vec4(color, vAlpha * opacity * falloff * falloff);
    #include <colorspace_fragment>
  }
`;

function particles(
  kind: ParticleKind,
): THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial> {
  const def = PARTICLES[kind];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(
      Array.from({ length: def.count * 3 }, Math.random),
      3,
    ),
  );
  geometry.setAttribute(
    'seed',
    new THREE.Float32BufferAttribute(
      Array.from({ length: def.count }, Math.random),
      1,
    ),
  );
  const points = new THREE.Points(
    geometry,
    new THREE.ShaderMaterial({
      vertexShader: PARTICLE_VERTEX,
      fragmentShader: PARTICLE_FRAGMENT,
      uniforms: {
        time: ambientTime,
        size: { value: def.size },
        box: { value: def.box },
        height: { value: def.height },
        wobble: { value: def.wobble },
        blink: { value: def.blink },
        viewHeight: { value: 1 },
        focus: { value: new THREE.Vector3() },
        drift: { value: new THREE.Vector3(...def.drift) },
        color: { value: new THREE.Color(def.color) },
        opacity: { value: def.opacity },
      },
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
    }),
  );
  points.frustumCulled = false;
  points.userData.def = def;
  return points;
}

// -------------------------------------------------------------------------------------------
// Wind
// -------------------------------------------------------------------------------------------

export const SWAYING = new Set<PieceId>([
  'tree',
  'castle/tree-large',
  'castle/tree-small',
  'graveyard/pine',
  'pirate/palm-straight',
  'pirate/palm-bend',
]);

/** A copy of a tree's material that bends with height in the wind. `height` is the mesh's own. */
export function sway(material: THREE.Material, height: number): THREE.Material {
  const swaying = material.clone();
  swaying.onBeforeCompile = (shader) => {
    shader.uniforms.windTime = ambientTime;
    shader.uniforms.windBend = { value: 0.035 / height };
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform float windTime;\nuniform float windBend;',
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vec3 root = modelMatrix[3].xyz;
        #ifdef USE_INSTANCING
          root += instanceMatrix[3].xyz;
        #endif
        float bend = windBend * transformed.y * transformed.y;
        float phase = windTime * 1.4 + root.x * 0.35 + root.z * 0.25;
        transformed.x += bend * (sin(phase) + 0.4 * sin(phase * 2.3));
        transformed.z += bend * 0.6 * cos(phase * 0.8);`,
      );
  };
  return swaying;
}

// -------------------------------------------------------------------------------------------
// Ground
// -------------------------------------------------------------------------------------------

/** Floor pieces mottled like the ground, each tile its own shade so they read as flagstones. */
export const FLOORS = new Set<PieceId>([
  'floor',
  'floor-detail',
  'dungeon/floor',
  'dungeon/floor-detail',
]);

export const DEFAULT_GROUND = '#6b8a4e';

const blank = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
blank.needsUpdate = true;
/**
 * The current map's ground colour per tile, as the ground surfaces render it at level 0. Every
 * mottled material reads it at a noise-shifted spot, so floor, terrain and grass meet raggedly.
 * `rect` is the first tile and the size in tiles.
 */
export const groundColors = {
  map: { value: blank as THREE.Texture },
  rect: { value: new THREE.Vector4(0, 0, 1, 1) },
};

const GROUND_NOISE = /* glsl */ `
  varying vec3 vGround;
  uniform float groundTiles;
  uniform sampler2D groundColorMap;
  uniform vec4 groundColorRect;
  vec4 groundColorAt(vec2 xz) {
    vec2 tile = xz / ${TILE_SIZE.toFixed(1)} + 0.5 - groundColorRect.xy;
    return texture2D(groundColorMap, tile / groundColorRect.zw);
  }
  float groundHash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float groundNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(groundHash(i), groundHash(i + vec2(1.0, 0.0)), u.x),
      mix(groundHash(i + vec2(0.0, 1.0)), groundHash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }
`;

/**
 * Patches a ground material (the plane, terrain, floor tiles) with broad world-space patches, banded
 * so they stay flat like the models. Every ground surface shares the noise, so it runs on across
 * joins, and near level 0 takes the colour of the tile at a shifted spot, so tile edges go ragged.
 * `tiles` also shades each tile apart.
 */
export function mottle<T extends THREE.Material>(
  material: T,
  tiles = false,
): T {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.groundTiles = { value: tiles ? 1 : 0 };
    shader.uniforms.groundColorMap = groundColors.map;
    shader.uniforms.groundColorRect = groundColors.rect;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vGround;')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vec4 groundAt = vec4(transformed, 1.0);
        #ifdef USE_INSTANCING
          groundAt = instanceMatrix * groundAt;
        #endif
        vGround = (modelMatrix * groundAt).xyz;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${GROUND_NOISE}`)
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        vec2 at = vGround.xz;
        if (vGround.y < 0.3) {
          vec2 shift = vec2(groundNoise(at / 1.5), groundNoise(at / 1.5 + 47.3)) - 0.5
            + 0.4 * (vec2(groundNoise(at / 0.5 + 9.1), groundNoise(at / 0.5 + 83.7)) - 0.5);
          vec4 here = groundColorAt(at);
          vec4 there = groundColorAt(at + shift * 1.4);
          if (here.a * there.a > 0.5) diffuseColor.rgb *= there.rgb / max(here.rgb, vec3(0.002));
        }
        float patches = 0.65 * groundNoise(at / 9.0) + 0.35 * groundNoise(at / 4.0 + 31.7);
        float band = floor(clamp((patches - 0.5) * 1.8 + 0.5, 0.0, 0.999) * 4.0) / 3.0;
        diffuseColor.rgb *= mix(vec3(0.8, 0.83, 0.9), vec3(1.1, 1.08, 1.0), band);
        vec2 tile = floor(at / ${TILE_SIZE.toFixed(1)} + 0.5);
        diffuseColor.rgb *= 1.0 + groundTiles * 0.12 * (groundHash(tile) - 0.5);`,
      );
  };
  return material;
}

// -------------------------------------------------------------------------------------------
// Environment
// -------------------------------------------------------------------------------------------

const SKY_VERTEX = /* glsl */ `
  varying vec3 vDir;
  void main() {
    vDir = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const SKY_FRAGMENT = /* glsl */ `
  uniform vec3 zenith;
  uniform vec3 horizon;
  uniform vec3 sunColor;
  uniform vec3 sunDir;
  uniform vec3 moonDir;
  uniform float sun;
  uniform float moon;
  varying vec3 vDir;
  void main() {
    vec3 d = normalize(vDir);
    vec3 color = mix(horizon, zenith, pow(clamp(d.y, 0.0, 1.0), 0.6));
    float s = max(dot(d, sunDir), 0.0);
    color += sunColor * sun * (0.45 * pow(s, 10.0) + smoothstep(0.9988, 0.9992, s));
    float m = max(dot(d, moonDir), 0.0);
    color += vec3(0.75, 0.8, 0.95) * moon * (0.1 * pow(m, 24.0) + smoothstep(0.9994, 0.9996, m));
    gl_FragColor = vec4(color, 1.0);
    #include <colorspace_fragment>
  }
`;

/**
 * Sky, fog, lights, lamp glow and ambient particles around a map, shared by the game, editor and
 * viewer. The hub's `cycle`s through the day; every other map holds its own look.
 */
export class Environment {
  private group = new THREE.Group();
  private hemi = new THREE.HemisphereLight();
  private sun = new THREE.DirectionalLight();
  private dome = new THREE.Mesh(
    new THREE.SphereGeometry(SKY_RADIUS, 32, 16),
    new THREE.ShaderMaterial({
      vertexShader: SKY_VERTEX,
      fragmentShader: SKY_FRAGMENT,
      uniforms: {
        zenith: { value: new THREE.Color() },
        horizon: { value: new THREE.Color() },
        sunColor: { value: new THREE.Color() },
        sunDir: { value: new THREE.Vector3() },
        moonDir: { value: new THREE.Vector3() },
        sun: { value: 0 },
        moon: { value: 0 },
      },
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
    }),
  );
  private stars: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial>;
  private pool: THREE.Mesh | null;
  /** The sky's `night`, shared by the lamp shaders. */
  readonly night = { value: 0 };
  private particles: THREE.Points<THREE.BufferGeometry, THREE.ShaderMaterial>[];
  private fog: THREE.Fog;
  private day: Sky;
  private sky: Sky;
  private sunDir = FIXED_SUN.clone();
  private buffer = new THREE.Vector2();

  constructor(
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    map: Pick<ArenaMap, 'env' | 'pieces'> = { pieces: [] },
    private cycle = false,
  ) {
    const env = map.env ?? {};
    this.day = daySky(env);
    this.sky = daySky(env);
    const [near, far] = env.fog ?? [70, 160];
    this.fog = new THREE.Fog(this.sky.horizon, near, far);
    scene.fog = this.fog;
    scene.add(this.group);

    this.group.add(this.hemi);
    this.sun.castShadow = renderer.shadowMap.enabled;
    // Low shadows (`createRenderer`) are the plain filter at half the resolution.
    this.sun.shadow.mapSize.setScalar(
      renderer.shadowMap.type === THREE.PCFSoftShadowMap ? 2048 : 1024,
    );
    const extent = 40;
    Object.assign(this.sun.shadow.camera, {
      left: -extent,
      right: extent,
      top: extent,
      bottom: -extent,
      near: 1,
      far: 140,
    });
    this.sun.shadow.bias = -0.0005;
    // Faces nearly edge-on to the light shadow themselves in diagonal stripes without this.
    this.sun.shadow.normalBias = 0.1;
    this.group.add(this.sun);

    this.dome.renderOrder = -1;
    this.dome.frustumCulled = false;
    this.stars = starField(renderer.getPixelRatio());
    this.dome.add(this.stars);
    this.group.add(this.dome);

    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(200, 48),
      mottle(
        new THREE.MeshLambertMaterial({ color: env.ground ?? DEFAULT_GROUND }),
      ),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.05;
    ground.receiveShadow = true;
    this.group.add(ground);

    const glow = lampMesh(map, false, this.night);
    this.pool = lampMesh(map, true, this.night);
    if (glow && this.pool) this.group.add(glow, this.pool);
    this.particles = (env.particles ?? []).map(particles);
    if (this.particles.length) this.group.add(...this.particles);
    this.apply();
  }

  /** Call once a frame: moves the sky with the camera and the particles with `focus`. */
  update(camera: THREE.Camera, focus: THREE.Vector3): void {
    ambientTime.value = performance.now() / 1000;
    if (this.cycle) {
      const hour = hourNow();
      skyAt(hour, this.day, this.sky);
      sunDirection(hour, this.sunDir);
      this.apply();
    }
    camera.getWorldPosition(this.dome.position);
    const viewHeight = this.renderer.getDrawingBufferSize(this.buffer).y;
    for (const points of this.particles) {
      points.material.uniforms.focus.value.copy(focus);
      points.material.uniforms.viewHeight.value = viewHeight;
    }
  }

  dispose(): void {
    this.scene.remove(this.group);
    this.group.traverse((child) => {
      if (!(child instanceof THREE.Mesh || child instanceof THREE.Points))
        return;
      child.geometry.dispose();
      (child.material as THREE.Material).dispose();
    });
  }

  private apply(): void {
    const { sky, sunDir } = this;
    // Static maps light from one fixed direction, a moon on night maps; the hub's moon opposes its sun.
    const moonDir = this.cycle
      ? new THREE.Vector3(-sunDir.x, -sunDir.y, sunDir.z)
      : sunDir;
    const up = this.cycle ? sunDir.y >= 0 : sky.night < 0.5;
    const light = (up ? sunDir : moonDir).clone();
    light.y = Math.max(light.y, MIN_LIGHT_ELEVATION);
    this.sun.position.copy(light.normalize()).multiplyScalar(60);
    // Fades out as the sun crosses the horizon, hiding the swap to the moon.
    const crossing = this.cycle
      ? THREE.MathUtils.smoothstep(Math.abs(sunDir.y), 0, 0.08)
      : 1;
    this.sun.color.copy(sky.sun);
    this.sun.intensity = sky.sunIntensity * crossing;
    this.hemi.color.copy(sky.hemiSky);
    this.hemi.groundColor.copy(sky.hemiGround);
    this.hemi.intensity = sky.hemiIntensity;
    this.fog.color.copy(sky.horizon);
    this.scene.background = sky.horizon;

    const above = (dir: THREE.Vector3) =>
      THREE.MathUtils.smoothstep(dir.y, -0.05, 0.05);
    const uniforms = this.dome.material.uniforms;
    uniforms.zenith.value.copy(sky.zenith);
    uniforms.horizon.value.copy(sky.horizon);
    uniforms.sunColor.value.copy(sky.sun);
    uniforms.sunDir.value.copy(sunDir);
    uniforms.moonDir.value.copy(moonDir);
    uniforms.sun.value = above(sunDir) * (1 - sky.night);
    uniforms.moon.value = above(moonDir) * sky.night;
    this.stars.material.opacity = sky.night;
    this.stars.visible = sky.night > 0.01;

    this.night.value = sky.night;
    if (this.pool) this.pool.visible = sky.night > 0.01;
    for (const points of this.particles) {
      const def: ParticleDef = points.userData.def;
      const shown =
        def.shows === 'day'
          ? 1 - sky.night
          : def.shows === 'night'
            ? sky.night
            : 1;
      points.material.uniforms.opacity.value = def.opacity * shown;
      points.visible = shown > 0.01;
    }
  }
}

/** Stars scattered over the upper sky, a little inside the dome. */
function starField(
  pixelRatio: number,
): THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> {
  const positions: number[] = [];
  const colors: number[] = [];
  for (let i = 0; i < STAR_COUNT; i++) {
    const y = 0.08 + Math.random() * 0.92;
    const r = Math.sqrt(1 - y * y);
    const angle = Math.random() * Math.PI * 2;
    const radius = SKY_RADIUS * 0.95;
    positions.push(
      Math.cos(angle) * r * radius,
      y * radius,
      Math.sin(angle) * r * radius,
    );
    const brightness = 0.35 + Math.random() * 0.65;
    colors.push(brightness, brightness, brightness);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  const stars = new THREE.Points(
    geometry,
    new THREE.PointsMaterial({
      size: 1.6 * pixelRatio,
      sizeAttenuation: false,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      fog: false,
      toneMapped: false,
    }),
  );
  stars.frustumCulled = false;
  return stars;
}
